/**
 * ReportCard - Instagram-style scent report card
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/utils/cn';
import { useSocialStore } from '@/stores/useSocialStore';
import type { ScentReport } from '@/types/social';

interface ReportCardProps {
  report: ScentReport;
  onUserClick?: (userId: string) => void;
  onReportClick?: (reportId: string) => void;
}

export function ReportCard({ report, onUserClick, onReportClick }: ReportCardProps) {
  const { likeReport, unlikeReport } = useSocialStore();
  const [isLiked, setIsLiked] = useState(report.is_liked || false);
  const [likesCount, setLikesCount] = useState(report.likes_count);
  const [isLiking, setIsLiking] = useState(false);

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLiking) return;

    setIsLiking(true);
    if (isLiked) {
      setIsLiked(false);
      setLikesCount((c) => c - 1);
      await unlikeReport(report.id);
    } else {
      setIsLiked(true);
      setLikesCount((c) => c + 1);
      await likeReport(report.id);
    }
    setIsLiking(false);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareText = `🎤 SCENT REPORT from ${report.location_name || 'Unknown'}
AQI: ${report.aqi || 'N/A'} | "${report.scent_notes || report.description || ''}"

Filed by Reporter ${report.user?.reporter_id || ''} via @blurp.ie
#InternetOfScent #ScentReporter

${window.location.origin}/report/${report.id}`;

    if (navigator.share) {
      await navigator.share({
        title: 'Scent Report - Blurp.ie',
        text: shareText,
        url: `${window.location.origin}/report/${report.id}`,
      });
    } else {
      // Copy to clipboard
      await navigator.clipboard.writeText(shareText);
      alert('Report link copied to clipboard!');
    }
  };

  const getAqiColor = (aqi: number | null) => {
    if (!aqi) return 'text-gray-400';
    if (aqi <= 50) return 'text-emerald-400';
    if (aqi <= 100) return 'text-yellow-400';
    if (aqi <= 150) return 'text-orange-400';
    return 'text-red-400';
  };

  const getAqiLabel = (aqi: number | null) => {
    if (!aqi) return 'Unknown';
    if (aqi <= 50) return 'Good';
    if (aqi <= 100) return 'Moderate';
    if (aqi <= 150) return 'Unhealthy';
    return 'Very Unhealthy';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'bg-glass-bg backdrop-blur-xl border border-glass-border rounded-2xl overflow-hidden',
        'hover:border-hud-primary/30 transition-colors cursor-pointer'
      )}
      onClick={() => onReportClick?.(report.id)}
    >
      {/* Header - User info */}
      <div className="p-4 flex items-center gap-3">
        <div
          className={cn("relative", report.user_id && "cursor-pointer")}
          onClick={(e) => {
            e.stopPropagation();
            if (report.user_id) onUserClick?.(report.user_id);
          }}
        >
          {report.user?.avatar_url ? (
            <img
              src={report.user.avatar_url}
              alt={report.user.username}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-hud-primary to-teal-600 flex items-center justify-center">
              <span className="text-lg">☁️</span>
            </div>
          )}
          {report.user?.is_verified_reporter && (
            <span className="absolute -bottom-1 -right-1 text-xs">✅</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={cn("font-semibold text-white truncate", report.user_id && "hover:underline cursor-pointer")}
              onClick={(e) => {
                e.stopPropagation();
                if (report.user_id) onUserClick?.(report.user_id);
              }}
            >
              {report.user?.username || 'Anonymous'}
            </span>
            {report.is_featured && (
              <span className="text-xs bg-pink-500/20 text-pink-400 px-2 py-0.5 rounded-full">
                🌟 Featured
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <span className="font-mono">{report.user?.reporter_id}</span>
            <span>•</span>
            <span>{formatDistanceToNow(new Date(report.created_at), { addSuffix: true })}</span>
          </div>
        </div>

        {/* AQI Badge */}
        {report.aqi && (
          <div className="text-right">
            <div className={cn('text-lg font-bold', getAqiColor(report.aqi))}>
              AQI {report.aqi}
            </div>
            <div className="text-xs text-text-secondary">{getAqiLabel(report.aqi)}</div>
          </div>
        )}
      </div>

      {/* Image (if exists) */}
      {report.image_url && (
        <div className="aspect-square bg-slate-800">
          <img
            src={report.image_url}
            alt="Scent report"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {/* Scent notes - the star of the show */}
        {report.scent_notes && (
          <div className="mb-3 p-3 bg-hud-primary/10 border border-hud-primary/20 rounded-xl">
            <div className="text-xs text-hud-primary mb-1 uppercase tracking-wider">Scent Notes</div>
            <p className="text-white">"{report.scent_notes}"</p>
          </div>
        )}

        {/* Description */}
        {report.description && (
          <p className="text-text-secondary mb-3">{report.description}</p>
        )}

        {/* Location & conditions */}
        <div className="flex flex-wrap gap-2 text-xs">
          {report.location_name && (
            <span className="bg-slate-700/50 px-2 py-1 rounded-full text-text-secondary">
              📍 {report.location_name}
            </span>
          )}
          {report.temperature && (
            <span className="bg-slate-700/50 px-2 py-1 rounded-full text-text-secondary">
              🌡️ {report.temperature}°C
            </span>
          )}
          {report.humidity && (
            <span className="bg-slate-700/50 px-2 py-1 rounded-full text-text-secondary">
              💧 {report.humidity}%
            </span>
          )}
          {report.pm25 && (
            <span className="bg-slate-700/50 px-2 py-1 rounded-full text-text-secondary">
              PM2.5: {report.pm25} µg/m³
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 pb-4 flex items-center gap-4">
        {/* Like */}
        <button
          onClick={handleLike}
          className={cn(
            'flex items-center gap-1.5 transition-colors',
            isLiked ? 'text-red-400' : 'text-text-secondary hover:text-red-400'
          )}
        >
          <motion.span
            animate={isLiked ? { scale: [1, 1.3, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            {isLiked ? '❤️' : '🤍'}
          </motion.span>
          <span className="text-sm">{likesCount}</span>
        </button>

        {/* Comment */}
        <button className="flex items-center gap-1.5 text-text-secondary hover:text-white transition-colors">
          <span>💬</span>
          <span className="text-sm">{report.comments_count}</span>
        </button>

        {/* Share */}
        <button
          onClick={handleShare}
          className="flex items-center gap-1.5 text-text-secondary hover:text-hud-primary transition-colors"
        >
          <span>📤</span>
          <span className="text-sm">Share</span>
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Verified report badge */}
        {report.is_verified_report && (
          <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-1 rounded-full">
            ✅ Verified Report
          </span>
        )}
      </div>
    </motion.div>
  );
}
